import { InlineSelect, Pagination } from '@design-system';
import { isPrefersReducedMotion } from '@utils/isPrefersReducedMotion';
import { isUpcoming } from '@utils/product';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import qs from 'query-string';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WORKS_INDEX_PAGE_TYPE, WORKS_PER_PAGE } from '../../constants';
import { pathFor } from '../../helpers/links';
import { getProductTileStatus } from '../../helpers/purchasing';
import { combineSorters, Sorter } from '../../helpers/sorting';
import { useIsSubscribed } from '../../hooks/subscription/useIsSubscribed';
import { useCountryCode } from '../../hooks/useCountryCode';
import type { ProductFiltersController } from '../../hooks/useProductFilters';
import { useProductFilters } from '../../hooks/useProductFilters';
import { useQueryParams } from '../../hooks/useQueryParams';
import { OrderSort, ProductsTab } from '../../types/Filters';
import {
  GeneratedGetDrawByProductIDsQuery,
  GeneratedSubscriptionType,
} from '../../types/generated';
import {
  TrackFiltersClicked,
  TrackPageView,
  TrackWorkClicked,
  TrackWorksResults,
} from '../../utils/analytics';
import { ProductFilterer } from '../../utils/filtering';
import { productUrl } from '../../utils/productUrl';
import { NavigationStateContext } from '../Containers/NavigationStateProvider/NavigationStateProvider';
import FullpageCta from '../Content/FullpageCTA';
import { OpenFiltersButton } from '../Filters/OpenFiltersButton';
import ProductFiltersBar from '../Filters/ProductFiltersBar';
import PageHeading from '../Layout/PageHeading';
import PageSection from '../Layout/PageSection';
import ProductTile from '../ProductTile';
import { SearchBar } from '../Search/SearchBar';
import Tabs from '../Tabs';

const ProductFiltersDrawer = dynamic(
  () => import('../Filters/ProductFiltersDrawer'),
  { ssr: false },
);

type Props = {
  allWorks: Product[];
  draws: GeneratedGetDrawByProductIDsQuery['getDrawByProductIDs'];
  blok: ProductsIndexPageType;
  pagination: {
    page: number;
    total: number;
  };
};

const Works = ({ allWorks, blok, draws, pagination }: Props) => {
  const { t } = useTranslation(['works', 'filters']);
  const router = useRouter();
  const headingRef = useRef<HTMLDivElement>(null);
  const [countryCode] = useCountryCode();
  const [products, setProducts] = useState(allWorks);
  const [hydrated, setHydrated] = useState(false);
  const [currentPage, setCurrentPage] = useState(pagination.page);
  const [numPages, setNumPages] = useState(pagination.total);
  const [isFilterPaneShown, setIsFilterPaneShown] = useState(false);
  const [paginationQuery, setPaginationQuery] = useState('');
  const { clearNavigationState, setNavigationState } = useContext(
    NavigationStateContext,
  );
  const [isOrderInteractedWith, setIsOrderInteractedWith] =
    useState<boolean>(false);

  const filters = useProductFilters();
  const filterer = useMemo(() => {
    return new ProductFilterer(
      allWorks.filter((work) => !work.excludeFromRegions.includes(countryCode)),
    );
  }, [products, countryCode]);

  const tabs = useProductTabs(filters);

  useEffect(() => {
    TrackPageView({
      page: WORKS_INDEX_PAGE_TYPE,
      properties: { tab: tabs.current },
    });
  }, []);

  useEffect(() => {
    if (isOrderInteractedWith) {
      TrackWorksResults({
        page: WORKS_INDEX_PAGE_TYPE,
        searchTerm: filters.searchTerm.value,
        results: productsToDisplay,
        tab: tabs.current,
        availibilityFilter: [filters.availability.value],
        finishFilters: filters.finish.values,
        mediumFilter: filters.mediums.values,
        orderFilter: filters.order.value,
        releaseFilters: filters.release.values,
        totalPages: numPages,
      });
    }
  }, [products, filters.order.value?.toString()]);

  useEffect(() => {
    setHydrated(true);

    // Delay updating products until we have the filters from the query params in the URL.
    if (!router.isReady) return;

    const nextProducts = filterer.filter({
      availability: filters.availability.value,
      shipping: filters.shipping.value,
      mediums: filters.mediums.values,
      searchTerm: filters.searchTerm.value,
      finishes: filters.finish.values,
      releaseTypes: filters.release.values,
    });

    if (filters.isInteractedWith) {
      TrackWorksResults({
        page: WORKS_INDEX_PAGE_TYPE,
        searchTerm: filters.searchTerm.value,
        results: nextProducts,
        tab: tabs.current,
        availibilityFilter: [filters.availability.value],
        finishFilters: filters.finish.values,
        mediumFilter: filters.mediums.values,
        orderFilter: filters.order.value,
        releaseFilters: filters.release.values,
        totalPages: numPages,
      });
    }

    // Get new total number of pages following the filter of the products
    setNumPages(Math.floor(nextProducts.length / WORKS_PER_PAGE) + 1);

    setPaginationQuery(
      window.location.search.split('?')[1]
        ? `?${window.location.search.split('?')[1]}`
        : '',
    );

    if (filters.isInteractedWith) {
      setCurrentPage(1);

      const currentUrl = new URL(window.location.href);
      const pathParts = currentUrl.pathname.split('/');
      const currentPageInUrl = pathParts[pathParts.length - 1];

      if (currentPageInUrl !== '1') {
        pathParts[pathParts.length - 1] = '1';
        const newPath = pathParts.join('/');
        router.push(newPath + currentUrl.search, undefined, { shallow: true });
      }
    }

    setProducts(() => nextProducts);
  }, [
    router.isReady,
    filters.availability.value?.toString(),
    filters.shipping.value?.toString(),
    filters.mediums.values?.toString(),
    filters.searchTerm.value?.toString(),
    filters.finish.values?.toString(),
    filters.release.values?.toString(),
    filters.order.value?.toString(),
    countryCode,
  ]);

  const sortedProducts = useMemo(() => {
    return sortProducts(products, filters.order.value);
  }, [products, filters.order.value?.toString()]);

  const productsToDisplay = sortedProducts.slice(
    (currentPage - 1) * WORKS_PER_PAGE,
    currentPage * WORKS_PER_PAGE,
  );

  const allProductsCount = useMemo(() => {
    return products.reduce((total, product) => {
      const productCount =
        product.productType === 'Collection' ? Number(product.editionSize) : 1;

      return total + productCount;
    }, 0);
  }, [products]);

  useEffect(() => {
    clearNavigationState();
    setNavigationState({
      type: 'PRODUCT',
      order: filters.order.value,
      query: filters.searchTerm.value,
      mediums: filters.mediums.values,
      shipping: filters.shipping.value,
      availability: filters.availability.value,
      finish: filters.finish.values,
      release: filters.release.values,
      count: allProductsCount,
      page: currentPage,
    });
  }, [
    setNavigationState,
    clearNavigationState,
    filters.order.value,
    filters.searchTerm.value,
    filters.mediums.values.join(','),
    filters.availability.value,
    filters.finish.values.join(','),
    filters.release.values.join(','),
    allProductsCount,
  ]);

  useEffect(() => {
    setCurrentPage(pagination.page);
  }, [pagination.page]);

  const scrollToTopOfIndex = useCallback(() => {
    if (headingRef.current === null) return;

    const heading = headingRef.current;
    const headerEndY = heading.offsetTop + heading.clientHeight;
    const paddingY = 72;
    const desiredY = headerEndY - paddingY;
    const currentY = window.scrollY;

    if (currentY > desiredY) {
      // Ensure new items are rendered before scrolling
      setTimeout(() => {
        window.scrollTo({
          top: desiredY,
          behavior: isPrefersReducedMotion() ? 'instant' : 'smooth',
        });
      }, 100);
    }
  }, []);

  const handleTabClick = useCallback(() => {
    filters.setIsInteractedWith(true);
    scrollToTopOfIndex();
  }, [scrollToTopOfIndex]);

  const activeFiltersCount =
    filters.mediums.values.length +
    filters.finish.values.length +
    filters.release.values.length;

  const upcomingProducts = productsToDisplay.filter((product) => {
    return isUpcoming(product);
  });

  const { checkIsSubscribed, refetch } = useIsSubscribed(
    upcomingProducts.map((product) => ({
      type: GeneratedSubscriptionType.Product,
      objectId: shopifyGraphqlIdToAdminId(product.id) || '',
    })),
  );

  return (
    <main className="my-9 space-y-9 md:my-12 md:space-y-12">
      <div ref={headingRef} className="relative space-y-2">
        <PageSection>
          <div className="flex w-full flex-1 flex-col space-y-6">
            <PageHeading
              heading={t('heading', { heading: blok.title })}
              strapline={t('subHeading', { subHeading: blok.subtitle })}
            />
            <div className="flex w-full flex-1 space-x-3">
              <div className="flex-1">
                <SearchBar
                  defaultValue={filters.searchTerm.value}
                  onChange={(value) => {
                    filters.searchTerm.set(value);
                    filters.setIsInteractedWith(true);
                  }}
                  placeholder={t('searchBarPlaceholderText')}
                />
              </div>
              <div className="shrink-0">
                <OpenFiltersButton
                  onOpen={() => {
                    setIsFilterPaneShown(true);
                    TrackFiltersClicked({
                      page: 'works index',
                      indexAvailabilityFilter: [filters.availability.value],
                      indexFinishingFilter: filters.finish.values,
                      indexMediumFilter: filters.mediums.values,
                      indexReleaseFilter: filters.release.values,
                      indexOrderFilter: filters.order.value,
                      indexSearchQuery: filters.searchTerm.value,
                      indexLength: allProductsCount,
                    });
                  }}
                />
              </div>
            </div>
            <ProductFiltersDrawer
              filters={filters}
              productCount={allProductsCount}
              setShown={setIsFilterPaneShown}
              shown={isFilterPaneShown}
            />
          </div>
        </PageSection>

        {activeFiltersCount > 0 && (
          <PageSection fullWidth>
            <ProductFiltersBar filters={filters} />
          </PageSection>
        )}
      </div>

      <div className="sticky top-14 z-40 !mt-3 w-full bg-white md:top-18">
        <PageSection>
          <Tabs
            activeTab={tabs.current}
            onClick={handleTabClick}
            tabs={[
              { key: 'ALL', label: 'All works', href: tabs.urls.ALL },
              { key: 'UPCOMING', label: 'Upcoming', href: tabs.urls.UPCOMING },
              { key: 'BUY_NOW', label: 'Buy now', href: tabs.urls.BUY_NOW },
            ]}
          />
        </PageSection>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-divider/20" />
      </div>

      <PageSection>
        <div className="space-y-6">
          {productsToDisplay.length > 0 && (
            <section className="flex items-center justify-between space-x-3">
              <div className="shrink-0">
                <span className="truncate text-neutral-4">
                  {t('worksCount', { count: allProductsCount })}
                </span>
              </div>
              <div className="shrink-0">
                <InlineSelect
                  onChange={(e) => {
                    filters.order.set(e.currentTarget.value as OrderSort);
                    setIsOrderInteractedWith(true);
                  }}
                  options={[
                    {
                      key: t('filters:products.sortRelevance'),
                      value: 'Relevance',
                    },
                    {
                      key: t('filters:products.sortReleaseDate'),
                      value: 'RecentReleaseDate',
                    },
                    {
                      key: t('filters:products.sortReleaseDateAsc'),
                      value: 'RecentReleaseDateAsc',
                    },
                    {
                      key: t('filters:products.sortPriceHighLow'),
                      value: 'PriceHighLow',
                    },
                    {
                      key: t('filters:products.sortPriceLowHigh'),
                      value: 'PriceLowHigh',
                    },
                  ]}
                  textAlign="right"
                  value={filters.order.value}
                />
              </div>
            </section>
          )}

          <section className="grid-section gap-y-6">
            {productsToDisplay?.length > 0 ? (
              <>
                {productsToDisplay.map((product, index) => {
                  const draw = draws.find(
                    (draw) =>
                      draw.productId === shopifyGraphqlIdToAdminId(product.id),
                  );

                  return (
                    <article
                      key={product.id + hydrated}
                      className="col-span-6 md:col-span-4 lg:col-span-3"
                      onClick={() =>
                        TrackWorkClicked({
                          page: WORKS_INDEX_PAGE_TYPE,
                          id: product.id,
                          productTitle: product.title,
                          artist: product.artist,
                          price: Number(product.price),
                          productType: product.productType,
                          productStatus: getProductTileStatus(product),
                          releaseDate: product.date,
                          releaseType: product.releaseType,
                          productUrl: product.full_slug
                            ? productUrl(product.full_slug, product.productType)
                            : '',
                          searchTerm: filters.searchTerm.value,
                          availibilityFilter: filters.availability.value
                            ? [filters.availability.value]
                            : [],
                          finishFilters: filters.finish.values,
                          mediumFilter: filters.mediums.values,
                          orderFilter: filters.order.value,
                          releaseFilters: filters.release.values,
                          position: index,
                          pageNumber: currentPage,
                          tab: tabs.current,
                        })
                      }
                    >
                      <ProductTile
                        draw={draw}
                        eagerLoading={index < 2}
                        isSubscribed={checkIsSubscribed(
                          shopifyGraphqlIdToAdminId(product.id),
                          GeneratedSubscriptionType.Product,
                        )}
                        onSubscribe={() => {
                          refetch();
                        }}
                        product={product}
                      />
                    </article>
                  );
                })}
                {numPages > 1 && (
                  <div className="col-span-12 pt-3 md:pt-6">
                    <Pagination
                      currentPage={currentPage}
                      numPages={numPages}
                      paginationUrl={pathFor({
                        page: 'WORKS_PAGINATION_ROOT_PATH',
                      })}
                      queryParams={paginationQuery}
                    />
                  </div>
                )}
              </>
            ) : (
              <p className="col-span-12 text-neutral-4">{t('noResultsBody')}</p>
            )}
          </section>
        </div>
      </PageSection>

      {allProductsCount === 0 && (
        <PageSection className="mb-12 md:mb-14">
          <FullpageCta body={t('ctaBody')} title={t('ctaHeader')} />
        </PageSection>
      )}
    </main>
  );
};

export default Works;

const sortByPriority = (
  artists: MultiCurrencyProduct[],
  priorityList: string[],
  depriorityList: string[],
) => {
  return artists.sort((a, b) => {
    const indexA = priorityList.indexOf(a.full_slug!);
    const indexB = priorityList.indexOf(b.full_slug!);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    if (indexA !== -1) {
      return -1;
    }
    if (indexB !== -1) {
      return 1;
    }

    const deindexA = depriorityList.indexOf(a.full_slug!);
    const deindexB = depriorityList.indexOf(b.full_slug!);
    if (deindexA !== -1 && deindexB !== -1) {
      return deindexA - deindexB;
    }
    if (deindexA !== -1) {
      return 1;
    }
    if (deindexB !== -1) {
      return -1;
    }

    return (
      new Date(b.launchDate!).getTime() - new Date(a.launchDate!).getTime()
    );
  });
};

const depriorityList = [
  'paul-insect-something-forbidden-blue',
  'paul-insect-super-glow',
  'yoon-hyup-city-fountain',
  'yoon-hyup-in-the-city',
  'piet-parra-lost-keys-four-walls',
  'night-in-paris-golden-light',
  'paul-insect-something-forbidden-gold',
  'pejac-hope-release-8',
  'vhils-blur',
  'beeple-choose-violence',
  'tide-sit',
  'javier-calleja-it-wasnt-me',
  'beeple-signal',
  'johnson-tsang-open-the-right-mind',
  'johnson-tsang-the-moment',
  'javier-calleja-floating',
  'yutaka-hashimoto-study_for_null_portrait-of-someone-166',
  'piet-parra-lost-keys',
  'vhils-dissipate',
  'johnson-tsang-soul-to-soul',
  'javier-calleja-diptych',
  'christmas-tree-baubles',
  'javier-calleja-in-my-life',
  'benrei-huang-minority-rules',
  'vista-series-2',
  'javier-calleja-once',
  'the-ultimate-journey-gold',
  'the-ultimate-journey-red',
  'the-ultimate-journey-blue',
  'together-alone-silver',
  'crystal-habits-gold',
  'crystal-habits-blue',
  'beeple-the-tree-of-knowledge-triptych',
  'sweetie-jar',
  'peace',
  'beeple-noise',
  'together-alone-gold',
  'javier-calleja-once-in-my-life',
  'second-chance-always',
  'the-observer-4',
  'first-look',
  'up-to-you',
  'the-observer-3',
  'the-observer-1',
  'the-reflection-red',
  'the-observer-2',
  'piet-parra-four-walls',
  'third-time-lucky',
  'the-reflection-blue',
  'cosmodela-duponti',
  'cosmodela',
  'slawn-ugly-bastard',
  'kai-content',
  'kai-content-hand-finished',
  'jr-la-nascita',
  'jr-giants-high-jump-in-rio',
  'baldur-helgason-menippean-satire',
  'picaresque',
  'deekay-love-ripples',
  'far-from-home',
  'standing-with-luv-small',
  'still-confused',
  'confused-luv-small',
  'with-his-sidekick-small',
  'this-is-luv-small',
  'confused-luv-medium',
  'standing-with-luv-medium',
  'ly-bloom-m',
  'ly-im-luv-m',
  'ly-luvs-burgers',
  'ly-living-with-luv',
  'ly-luvs-dog-park',
  'ly-luv-somewhere-new',
  'jake-fried-open-eyes-signal',
  'jake-fried-open-eyes-flower',
  'snowfro-chromie-squiggle-8107',
  'cryptopunks-punk-on-chain',
  'cryptopunks-10000-on-chain',
  'katie-hector-ecstacy',
  'ly-bloom-xs',
  'test-draw-with-crypto',
  'hope-triptych',
  'pejac-hope-release-4',
  'hajime-sorayama-untitled',
  'nite-nite-black',
  'pejac-hope-release-5',
  'guzo-black-marble',
  'slingshot-requiem-print',
  '63-paul-insect-studio-2021',
  'javier-calleja-first-look',
  'ly-living-with-luv-showcards',
  'kukwon-woo-valley-of-the-shadow-of-death',
  'ly-im-luv-s',
  'ly-bloom-l',
  'ly-im-luv-l',
  'rabbit-car-on-the-head',
  'maze-print',
  'empathy-1',
  'green-master-bloom',
  'guzo-white-marble',
  'ly-bloom-xl',
  'sweetie-drawing-2-2021',
  'ground-dragon',
  'pejac-hope-release-7',
  'slingshot-aurum-jj',
  'so-youn-lee-ready',
  'thank-you-plastic-candy',
  'boys-dont-cry',
  '3-the-hard-way-hebru',
  'natalie',
  'katie-hector-new-hope',
  'kukwon-woo-breakfast-on-the-beach',
  'water-makes-up-most-of-the-human-body-green',
  'jack-kaido-icymi-2024',
  'bath-saxum',
  'hikari-shimoda-children-of-this-planet-38',
  'guzo-ocean',
  'nite-nite-white',
  'ly-luv-blossoming',
  'yu-nagaba-fishbowl',
  'james-jean-descendent-lucid-dreamer',
  'maze-aurum',
  'kukwon-woo-valley-of-the-shadow-of-death-breakfast-on-the-beach-diptych',
  '52-paul-insect-studio-2021',
  'water-makes-up-most-of-the-human-body-pink',
  'maze',
  'paul-insect-sweetie-drawing-1-2021',
  'takahiro-komuro-sphinx-kira',
  'the-last-evening',
  'test-gg-2',
  'minhee-kim-teresa',
  'descendent-verdigris',
  'thank-you-plastic-cup-noodle',
  'james-jean-woodcutter-wood',
  'slingshot-saxum',
  '34-paul-insect-studio-2021',
  'ly-im-luv-xs',
  'pejac-hope-release-1',
  'i-beg-your-garden',
  'obsession-no-joke-1',
  'gaia-white-marble',
  'besito',
  'crypto-princess',
  'miss-yvonne-my-next-door-neighbour',
  'umma',
  'sumergidos',
  'studio-2022',
  'paul-insect-mixed-media-2021',
  'taku-obata-b-boy-jushi-preorder',
  'minhee-kim-langley',
  'ly-bloom-s',
  'chen-wei-ting-poetic-fragments',
  'takeru-amano-venus',
  'midnight-ride',
  'rooo-lou-earnest',
  'pejac-hope-release-2',
  'taku-obata-b-boy-seidou',
  'taku-obata-b-boy-jushi',
  '3-the-hard-way-sculpture',
  '39-paul-insect-studio-2021',
  'woodcutter-bronze',
  'water-makes-up-most-of-the-human-body-blue',
  'ayako-rokkaku-untitled',
  'slingshot-requiem-relief',
  '35-paul-insect-studio-2021',
  'passage',
  'waiting-west-4th',
  'we-are-the-world',
  'felax',
  'nksin-my-david-x',
  'pejac-hope-release-6',
  'paul-insect-automaton-kid-2-aged-11-pi-the-dreamer-2021',
  '6-paul-insect-studio-2021',
  'free-advice-of-the-period',
  'ly-im-luv-xl',
  'paul-insect-special-print-edition',
  'sweet-junkfood',
  'nanohana-ramen',
  'yu-nagaba-toy-car',
  'paul-insect-sweet-jar-6-2021',
  'ayako-rokkaku-untitled-1',
  'giorgiko-unacquainted-friends',
  'free-advice',
  'bath-marble',
  'james-jean-maze-lucid',
  'paul-insect-eyes-on-paper-nl1-2021',
  'water-makes-up-most-of-the-human-body-yellow',
  'guzo-forest',
  'pejac-hope-release-3',
  'gaia-black-marble',
  'paul-insect-automaton-kid-4-age-12-halloween-dreamer-2021',
  'billy-big-ears-1',
  'white-rhino',
  'slingshot',
  'moving-colours',
  'ayako-rokkako-untitled-coa',
  'sir-joan-cornella-work-hard-and-then-die',
  'james-jean-sun-tarot-nebula-nectar-print',
  'james-jean-sun-tarot-nebula-pollen-sun-tarot-nebula-pollen-print',
  'james-jean-sun-tarot-nebula-nectar-sun-tarot-nebula-nectar-print',
  'james-jean-sun-tarot-nebula-print',
  'james-jean-sun-tarot-nebula-sun-tarot-nebula-print',
  'james-jean-sun-tarot-nebula-pollen-print',
  'kukwon-woo-waterfall-kirifuri',
  'kukwon-woo-waterfall-ono',
  'kukwon-woo-waterfall-amida',
  'kukwon-woo-waterfall-roben',
  'cleon-peterson-shame',
  'cleon-peterson-artist-bag',
  'the-return-in-aeternum',
  'the-return',
  'william-blake-night-the-first',
  'william-blake-night-the-second',
  'william-blake-night-the-third',
  'pejac-self-defence-1am',
  'pejac-self-defence-2am',
  'pejac-self-defence-3am',
  'pejac-self-defence-4am',
  'pejac-self-defence-5am',
  'pejac-self-defence-6am',
  'pejac-self-defence-7am',
  'pejac-self-defence-8am',
  'pejac-self-defence-9am',
  'pejac-self-defence-10am',
  'pejac-self-defence-11am',
  'pejac-self-defence-12pm',
  'pejac-self-defence-1am-special-edition',
  'pejac-self-defence-2am-special-edition',
  'pejac-self-defence-3am-special-edition',
  'pejac-self-defence-4am-special-edition',
  'pejac-self-defence-5am-special-edition',
  'pejac-self-defence-6am-special-edition',
  'pejac-self-defence-7am-special-edition',
  'pejac-self-defence-8am-special-edition',
  'pejac-self-defence-9am-special-edition',
  'pejac-self-defence-10am-special-edition',
  'pejac-self-defence-11am-special-edition',
  'pejac-self-defence-12pm-special-edition',
  'pejac-self-defence-set-main-editions',
  'em-cooper-in-the-middle-of-a-dream',
  'em-cooper-early-in-the-morning',
  'em-cooper-early-in-the-morning-in-the-middle-of-a-dream',
  'joakim-ojanen-the-dance-we-dance-when-we-are-together',
  'ferrari-sheppard-put-some-red-on-it',
  'parra-in-your-detached-world',
  'parra-il-senso-di-colpa',
  'backside-works-queen',
  'backside-works-princess',
  'yoon-hyup-ambre-roulette',
  'ly-luv-new-beginnings',
  'miwa-komatsu-three-guardians-gatekeeping-the-heat-and-cold',
];

// TODO: find a way to reduce renders or fix memoisation - massive number of calls
const sortProducts = (products: Product[], order: OrderSort) => {
  const sorted = [...products];

  if (order === 'Relevance') {
    const sorter = combineSorters([dateSorterDesc, artistNameSorter]);
    const prioritised = sorted
      .filter((p) => p.availability !== 'Coming soon')
      .sort(sorter);
    const upcoming = sorted
      .filter((p) => p.availability === 'Coming soon')
      .sort(sorter);

    return sortByPriority([...prioritised, ...upcoming], [], depriorityList);
  }

  if (order === 'PriceHighLow')
    return sorted.sort(combineSorters([priceHighLowSorter, artistNameSorter]));

  if (order === 'PriceLowHigh')
    return sorted.sort(combineSorters([priceLowHighSorter, artistNameSorter]));

  if (order === 'RecentReleaseDateAsc')
    return sorted.sort(combineSorters([dateSortedAsc, artistNameSorter]));

  return sorted.sort(combineSorters([dateSorterDesc, artistNameSorter]));
};

const priceHighLowSorter: Sorter<MultiCurrencyProduct> = (a, b) => {
  return (
    parseInt(a.price, 10) &&
    parseInt(b.price, 10) &&
    parseInt(b.price, 10) - parseInt(a.price, 10)
  );
};

const priceLowHighSorter: Sorter<MultiCurrencyProduct> = (a, b) => {
  return (
    parseInt(a.price, 10) &&
    parseInt(b.price, 10) &&
    parseInt(a.price, 10) - parseInt(b.price, 10)
  );
};

const dateSorterDesc: Sorter<MultiCurrencyProduct> = (a, b) => {
  const firstDate = a.date || '1970-01-01T00:00:00Z';
  const secondDate = b.date || '1970-01-01T00:00:00Z';
  return secondDate.localeCompare(firstDate);
};

const dateSortedAsc: Sorter<MultiCurrencyProduct> = (a, b) => {
  const firstDate = b.date || '1970-01-01T00:00:00Z';
  const secondDate = a.date || '1970-01-01T00:00:00Z';
  return secondDate.localeCompare(firstDate);
};

const artistNameSorter: Sorter<MultiCurrencyProduct> = (a, b) => {
  return a.artist.localeCompare(b.artist);
};

const useProductTabs = (filters: ProductFiltersController) => {
  const query = useQueryParams();

  const current: ProductsTab =
    filters.availability.value === 'Upcoming'
      ? 'UPCOMING'
      : filters.availability.value === 'Available'
        ? 'BUY_NOW'
        : 'ALL';

  // TODO: refactor
  const tabQuery = { ...query } as any;
  delete tabQuery.availability;
  delete tabQuery.order;
  const tabQs = qs.stringify(tabQuery);
  const withFiltersQuery = (path: string) => {
    if (tabQs === '') return path;
    if (path.includes('?')) return `${path}&${tabQs}`;

    return `${path}?${tabQs}`;
  };

  const urls: Record<ProductsTab, string> = {
    'ALL': withFiltersQuery('/products/1'),
    'UPCOMING': withFiltersQuery(
      '/products/1?availability=Upcoming&order=RecentReleaseDateAsc',
    ),
    'BUY_NOW': withFiltersQuery(
      '/products/1?availability=Available&order=RecentReleaseDate',
    ),
  };

  return {
    current,
    urls,
  };
};
